import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Playback } from "../lib/player/playback";
const mss = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
// Ticks sit in their own strip above the range input, so a drag that starts on
// the bar never lands on a tick, and a tick click never reaches the input.
function MarkerStrip({ markers, durationSec, onSeek }) {
    if (!(durationSec > 0) || !markers.length)
        return null;
    return (_jsx("div", { className: "player-markers", "data-testid": "dragon-player-markers", children: markers.filter((m) => m.atSec >= 0 && m.atSec <= durationSec).map((m) => (_jsx("button", { type: "button", className: `player-marker player-marker-${m.kind}`, style: { left: `${(m.atSec / durationSec) * 100}%` }, "aria-label": `${m.label} at ${mss(m.atSec)}`, title: `${m.label} at ${mss(m.atSec)}`, onClick: () => onSeek(m.atSec) }, `${m.kind}:${m.atSec}`))) }));
}
// Static feature detect: does the browser even expose VideoDecoder? Used as
// a fast path — if not, skip straight to <video> without spinning up
// Playback. Browsers that DO expose VideoDecoder may still fail per-clip
// (Safari WebCodecs rejects avc1.640032 high-profile at runtime); the
// useState fallback below handles that.
function webCodecsSupported() {
    if (typeof window === "undefined")
        return false;
    return "VideoDecoder" in window;
}
export default function DragonPlayer({ src, ev, onError, poster, hudLabel, startAtSec = 0, onSeekSettled, onEnded, onPlayingChange, onScrubChange, markers = [], onTimeUpdate, startMinDurationSec = 0 }) {
    const canvasRef = useRef(null);
    const playBtnRef = useRef(null);
    const seekRef = useRef(null);
    const timeRef = useRef(null);
    const exportBtnRef = useRef(null);
    const exportStatusRef = useRef(null);
    const videoRef = useRef(null);
    const playbackRef = useRef(null);
    const [supported] = useState(() => webCodecsSupported());
    // Flips when the WebCodecs Playback fires its internal onError. Causes
    // a re-render that swaps the <canvas> + controls subtree for a native
    // <video> with built-in controls. Safari plays cs256's avc1.640032
    // clips fine via the system H.264 decoder even when its WebCodecs
    // VideoDecoder refuses to configure them.
    const [fallback, setFallback] = useState(false);
    // `loading` shows a spinner overlay over the (still-black) canvas until
    // the decoder paints the first frame. Clip MP4s are 1–25 MB so there
    // can be a 1–3 s gap between click and visible content; without this
    // the operator sees an unexplained black rectangle.
    const [loading, setLoading] = useState(true);
    // Known clip length for marker placement; 0 until Playback (or the native
    // <video>) knows it. Fragmented clips stay 0 until the tail index settles.
    const [durationSec, setDurationSec] = useState(0);
    // Latest callbacks, read from inside the retry loop without re-mounting
    // Playback (which would restart the download).
    const seekSettledRef = useRef(onSeekSettled);
    seekSettledRef.current = onSeekSettled;
    // The native leg cannot distinguish the citation seek from a later user
    // scrub, so only the first landing is reported: otherwise dragging the bar
    // rewrites the disclosure into a claim about the citation that is false.
    const citationReported = useRef(false);
    const endedRef = useRef(onEnded);
    endedRef.current = onEnded;
    const playingRef = useRef(onPlayingChange);
    playingRef.current = onPlayingChange;
    const scrubRef = useRef(onScrubChange);
    scrubRef.current = onScrubChange;
    const timeUpdateRef = useRef(onTimeUpdate);
    timeUpdateRef.current = onTimeUpdate;
    // Reports only the settled length (ADR-172): 0 while a fragmented clip's
    // length is unknown or still a byte-share estimate.
    // A marker click is a jump, not playback: it is reported as a scrub that
    // ends after the landing's time update, so callers that act on playback
    // crossing a moment (early advance) never see the seek as a crossing.
    const markerJump = useRef(false);
    const onPlaybackTime = useCallback((sec) => {
        timeUpdateRef.current?.(sec, playbackRef.current?.knownDurationSec ?? 0);
        if (markerJump.current) {
            markerJump.current = false;
            scrubRef.current?.(false);
        }
    }, []);
    // Stable wrappers so Playback is not re-mounted when the callbacks change.
    const onPlaybackPlaying = useCallback((v) => { playingRef.current?.(v); }, []);
    const onPlaybackScrub = useCallback((v) => { scrubRef.current?.(v); }, []);
    const onCanvasError = useCallback(() => {
        // Failover to the native <video> is the same viewing, not a stop: no
        // play-state report here. The <video> reports its own play/pause.
        setFallback(true);
    }, []);
    const onFirstFrame = useCallback(() => {
        setLoading(false);
    }, []);
    const onPlaybackEnded = useCallback(() => {
        endedRef.current?.();
    }, []);
    // A destroyed player is neither playing nor scrubbing. Without this the
    // caller would keep the last "playing" forever once the player unmounts.
    useEffect(() => () => {
        playingRef.current?.(false);
        scrubRef.current?.(false);
    }, [src]);
    // Reset fallback + loading flags whenever the source changes — fresh src
    // is a fresh shot at the canvas path and a fresh download cycle.
    useEffect(() => {
        setFallback(false);
        setLoading(true);
        setDurationSec(0);
        citationReported.current = false;
    }, [src]);
    // Playback learns the length asynchronously and has no callback for it, and
    // the length can still move after it settles (fragmented body-complete, a
    // refined tail read). Poll fast until known, then slowly while markers exist,
    // so ticks track the same length the scrubber draws against.
    const wantMarkers = markers.length > 0;
    useEffect(() => {
        if (!supported || fallback || !wantMarkers)
            return;
        let timer;
        const poll = () => {
            const known = playbackRef.current?.knownDurationSec ?? 0;
            setDurationSec(known);
            timer = setTimeout(poll, known > 0 ? 1000 : 250);
        };
        timer = setTimeout(poll, 250);
        return () => clearTimeout(timer);
    }, [src, supported, fallback, wantMarkers]);
    // Mount Playback on canvas; teardown on unmount or src change. The
    // generation guard inside Playback + AbortController + idempotent
    // destroy make this strict-mode-safe (decision [#5]).
    useEffect(() => {
        if (!supported)
            return;
        if (fallback)
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const playback = new Playback({
            canvas,
            playBtn: playBtnRef.current,
            seekEL: seekRef.current,
            timeEL: timeRef.current,
            exportBtn: exportBtnRef.current,
            exportStatus: exportStatusRef.current,
        }, src, {
            fallbackCameraName: ev ? `Cam ${ev.bCamera}` : hudLabel?.camera,
            onError: onCanvasError,
            onFirstFrame,
            onEnded: onPlaybackEnded,
            onPlayingChange: onPlaybackPlaying,
            onScrubChange: onPlaybackScrub,
            onTimeUpdate: onPlaybackTime,
        });
        playbackRef.current = playback;
        return () => {
            playback.destroy();
            if (playbackRef.current === playback)
                playbackRef.current = null;
        };
        // ev is intentionally NOT a dep: we only need it at mount time for the
        // fallback name. If ev changes the parent re-keys this component anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, supported, fallback, onCanvasError, onFirstFrame, onPlaybackEnded, onPlaybackPlaying, onPlaybackScrub, onPlaybackTime]);
    // Citation seek. seek() clamps to whatever has streamed in, and it always
    // lands on the keyframe at or before the target, so "did the playhead reach
    // the request" is not a usable settle condition — it usually never will.
    // Instead wait until the target's bytes have arrived (bufferedMaxSec), then
    // seek exactly once. Seeking on every tick would reset the decoder four
    // times a second and yank the picture back while the reviewer watches.
    //
    // There is deliberately no timeout that seeks anyway: on a slow stream that
    // would land near zero and report it as the citation's landing, which is a
    // false statement about where the agent looked. Waiting leaves the honest
    // "Seeking to Ns…" message up, and a stream that never arrives fails through
    // Playback's own error path instead.
    useEffect(() => {
        if (!supported || fallback || startAtSec <= 0)
            return;
        const timer = setInterval(() => {
            const playback = playbackRef.current;
            if (!playback)
                return;
            // A minimum length needs the settled length first (never an estimate,
            // ADR-172); a shorter clip plays whole.
            if (startMinDurationSec > 0) {
                const known = playback.knownDurationSec;
                if (!(known > 0))
                    return;
                if (known < startMinDurationSec) {
                    clearInterval(timer);
                    seekSettledRef.current?.(playback.playheadSec, startAtSec, true);
                    return;
                }
            }
            // A clip shorter than its citation can never reach the target, so aim at
            // its end instead. durationSec is known from the moov while the body is
            // still arriving, so the wait is still on bytes, not on the duration.
            const target = playback.durationSec > 0 ? Math.min(startAtSec, playback.durationSec) : startAtSec;
            if (playback.bufferedMaxSec < target)
                return;
            clearInterval(timer);
            playback.seek(target);
            seekSettledRef.current?.(playback.playheadSec, startAtSec);
        }, 250);
        return () => clearInterval(timer);
    }, [src, supported, fallback, startAtSec, startMinDurationSec]);
    // Native fallback path. Two ways to land here:
    //   1. webCodecsSupported() was false at mount (Firefox without the flag,
    //      old Safari, etc.) — `supported` is false.
    //   2. WebCodecs was advertised but failed at runtime (Safari rejecting
    //      avc1.640032 high-profile at first decode) — `fallback` flipped
    //      via Playback's internal onError.
    // The HUD comes from the event row only in this path; canvas-burned-in
    // SEI HUD only exists on the WebCodecs path.
    if (!supported || fallback) {
        return (_jsxs("div", { className: "player-stage", children: [_jsx("video", { ref: videoRef, controls: true, autoPlay: true, 
                    // Most browsers (iOS Safari especially) gate autoplay-with-sound
                    // behind a user gesture. Mute lets the autoplay attribute fire on
                    // load. mpengine clips don't carry audio anyway.
                    muted: true, 
                    // iOS Safari would fullscreen any non-inline <video> on play —
                    // breaks the embedded player layout.
                    playsInline: true, preload: "metadata", poster: poster ?? undefined, onError: () => { playingRef.current?.(false); scrubRef.current?.(false); onError?.(); }, onEnded: () => { playingRef.current?.(false); onEnded?.(); }, onPlay: () => playingRef.current?.(true), onPause: () => playingRef.current?.(false), onTimeUpdate: (e) => { const d = e.currentTarget.duration; timeUpdateRef.current?.(e.currentTarget.currentTime, Number.isFinite(d) && d > 0 ? d : 0); }, onSeeking: () => scrubRef.current?.(true), onDurationChange: (e) => { const d = e.currentTarget.duration; setDurationSec(Number.isFinite(d) && d > 0 ? d : 0); }, onLoadedMetadata: (e) => {
                        if (startAtSec <= 0)
                            return;
                        const video = e.currentTarget;
                        if (startMinDurationSec > 0 && !(video.duration >= startMinDurationSec)) {
                            citationReported.current = true;
                            seekSettledRef.current?.(video.currentTime, startAtSec, true);
                            return;
                        }
                        video.currentTime = Number.isFinite(video.duration) ? Math.min(startAtSec, video.duration) : startAtSec;
                    }, onSeeked: (e) => {
                        scrubRef.current?.(false);
                        if (citationReported.current)
                            return;
                        citationReported.current = true;
                        seekSettledRef.current?.(e.currentTarget.currentTime, startAtSec);
                    }, style: { width: "100%", height: "100%", objectFit: "contain" }, children: _jsx("source", { src: src, type: "video/mp4" }) }), (ev || hudLabel) && (_jsxs("div", { className: "clip-hud", "aria-hidden": "true", children: [_jsx("span", { className: "clip-hud-cam", children: hudLabel ? hudLabel.camera : `Cam ${ev.bCamera}` }), _jsx("span", { className: "clip-hud-time", children: hudLabel ? hudLabel.time : new Date(ev.tEventStart).toLocaleString() })] })), _jsx(MarkerStrip, { markers: markers, durationSec: durationSec, onSeek: (sec) => { if (videoRef.current)
                        videoRef.current.currentTime = sec; } })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "player-stage", children: [_jsx("canvas", { ref: canvasRef, "data-testid": "dragon-player-canvas", 
                        // CSS sizes the visible element; the canvas's intrinsic width/height
                        // is reset on each rendered frame to match the decoded VideoFrame.
                        style: { width: "100%", height: "100%", objectFit: "contain" } }), loading && poster && (
                    // Poster covers the black canvas until the decoder paints frame one.
                    _jsx("img", { className: "player-poster", "data-testid": "dragon-player-poster", src: poster, alt: "", "aria-hidden": "true", style: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" } })), loading && (_jsxs("div", { className: "player-loading", "data-testid": "dragon-player-loading", "aria-live": "polite", children: [_jsx("span", { className: "spinner", "aria-hidden": "true" }), _jsx("span", { className: "copy", children: "Loading clip\u2026" })] }))] }), _jsxs("div", { className: "player-controls", "data-testid": "dragon-player-controls", children: [_jsx("button", { ref: playBtnRef, type: "button", className: "player-controls-play", "data-testid": "dragon-player-play", "aria-label": "Play or pause", children: "Play" }), _jsxs("div", { className: "player-controls-seekwrap", children: [_jsx(MarkerStrip, { markers: markers, durationSec: durationSec, onSeek: (sec) => { const p = playbackRef.current; if (!p)
                                    return; markerJump.current = true; scrubRef.current?.(true); p.seek(sec); } }), _jsx("input", { ref: seekRef, type: "range", min: 0, max: 1000, defaultValue: 0, className: "player-controls-seek", "data-testid": "dragon-player-seek", "aria-label": "Seek" })] }), _jsx("span", { ref: timeRef, className: "player-controls-time", "data-testid": "dragon-player-time", children: "0:00 / 0:00" }), _jsx("button", { ref: exportBtnRef, type: "button", className: "player-controls-export", "data-testid": "dragon-player-export", "aria-label": "Download clip with overlay", title: "Download .mp4 with HUD + detection overlays burned in", children: "Download with overlay" })] }), _jsx("span", { ref: exportStatusRef, className: "player-export-status", "data-testid": "dragon-player-export-status", role: "status", "aria-live": "polite" })] }));
}
